<template>
  <q-dialog v-model="shown">
    <q-card style="min-width: 70%;">
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Link To Sites
        </div>
        <q-space />
        <q-btn
          v-if="sitesWithoutLibraryChildren.length > 0"
          class="gt-xs"
          :label="selectedAll ? 'Deselect All' : 'Select All'"
          :disable="model.loading || model.dirty"
          @click="selectAll()"
        />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-expansion-item
          v-for="(circuitSites, circuit) in circuitsWithSites"
          :key="circuit"
          class="full-width"
          :disable="$_.keys(circuitsWithSites).length === 1"
          :default-opened="$_.keys(circuitsWithSites).length === 1"
          expand-separator
          popup
        >
          <template #header>
            <q-item-section
              class="col"
            >
              <div class="text-h7">
                <!-- If we are on the Library circuit, circuitsWithSites will include circuit as name -->
                <!-- If we are on a normal circuit, circuitsWithSites circuit is null -->
                {{ $_.keys(circuitsWithSites).length === 1 ? currentCircuit.name : circuit }}
                <q-badge
                  :color="circuitBadgeColor(circuitSites)"
                  floating
                >
                  {{ `${alreadyLinkedCount(circuitSites)}/${circuitSites.length}` }}
                </q-badge>
              </div>
            </q-item-section>
            <q-space />
            <q-btn
              v-if="unlinkedSites(circuitSites).length > 0"
              class="gt-xs"
              :label="selectedAllSites(unlinkedSites(circuitSites)) ? 'Deselect All' : 'Select All'"
              :disable="model.loading || model.dirty"
              @click="selectAll(circuitSites)"
            />
          </template>
          <q-list
            bordered
            separator
          >
            <q-item
              v-for="site in $_.sortBy(circuitSites, site => site.isLibrary ? '0' : site.label)"
              :key="site.id"
              clickable
              :class="{ 'bg-grey-3': alreadyLinked(site) }"
              @click="toggle(site)"
            >
              <q-item-section>
                <q-checkbox
                  :model-value="alreadyLinked(site) || isSelected(site) "
                  :label="site.label"
                  style="pointer-events: none;"
                  :disabled="alreadyLinked(site)"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-expansion-item>
      </q-card-section>
      <q-card-actions class="row justify-end q-ma-sm">
        <q-btn
          :label="linkLabel"
          color="primary"
          :disable="selected.length === 0"
          :loading="loading"
          @click="duplicateItems"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>

import { Notify } from 'quasar'

export default {
  name: 'LinkToSitesDialog',
  configuration: ['currentCircuit'],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    circuitsWithSites: {
      type: Object,
      required: true
    },
    model: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      selected: [],
      loading: false
    }
  },
  computed: {
    linkLabel () {
      return 'Link to Sites'
    },
    sitesWithoutLibraryChildren () {
      return _.filter(this.sites, (site) => !_.includes(this.model.libraryChildSiteIds, site.id))
    },
    selectedAll () {
      return this.selected.length === this.sitesWithoutLibraryChildren.length
    },
    sites () {
      return _.flatten(_.values(this.circuitsWithSites))
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    isSelected (site) {
      return _.includes(this.selected, site.id)
    },
    toggle (site) {
      if (this.isSelected(site)) {
        this.selected = _.without(this.selected, site.id)
      } else {
        this.selected.push(site.id)
      }
    },
    selectAll (sites = this.sitesWithoutLibraryChildren) {
      if (this.selectedAllSites(this.unlinkedSites(sites))) {
        this.selected = _.difference(this.selected, sites.map(site => site.id))
      } else {
        this.selected = _.union(this.selected, sites.map(site => site.id))
      }
    },
    selectedAllSites (sites) {
      return _.difference(sites.map(site => site.id), this.selected).length === 0
    },
    alreadyLinked (site) {
      return _.includes(this.model.libraryChildSiteIds, site.id)
    },
    alreadyLinkedCount (sites) {
      return _.sumBy(sites, site => this.alreadyLinked(site) || this.isSelected(site) ? 1 : 0)
    },
    unlinkedSites (sites) {
      return _.filter(sites, site => !this.alreadyLinked(site))
    },
    circuitBadgeColor (sites) {
      const librarySite = _.find(sites, 'isLibrary')

      // If there is no library site, use neutral color
      if (!librarySite) return 'grey'

      // If there is a library site and it is in use, return positive color
      if (this.isSelected(librarySite) || this.alreadyLinked(librarySite)) return 'positive'

      // If there is a library site and it is not in use, return warning color
      // This means linked sites are not using their circuit library for model links, and are linking directly
      return 'warning'
    },
    async duplicateItems () {
      if (!this.selected.length) return

      const model = this.model

      const siteIds = this.selected

      this.loading = true

      const { errors: duplicationErrors } = await model.duplicateModelToSites(model.id, siteIds)

      this.loading = false

      if (duplicationErrors && duplicationErrors.length > 0) {
        Notify.create({
          message: 'Error duplicating items',
          type: 'negative',
          position: 'top'
        })
        console.log(duplicationErrors)
      } else {
        model.fetch(true)
        this.shown = false
      }
    }
  }
}

</script>

<style lang="scss" scoped>
</style>
